import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Maximize2 } from "lucide-react";
// Note: For Portfolio Size we intentionally show a plain number (no symbol)

interface PortfolioSizeInputProps {
  portfolioSize: number;
  onPortfolioSizeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  totalAllocations?: number;
  hasOverflow?: boolean;
  onAutoSize?: () => void;
  isLocked?: boolean;
  currency?: string;
}

export const PortfolioSizeInput = ({
  portfolioSize,
  onPortfolioSizeChange,
  totalAllocations = 0,
  hasOverflow = false,
  onAutoSize,
  isLocked = false,
  currency = 'USD',
}: PortfolioSizeInputProps) => {
  // Debug logging to track auto-size button visibility
  console.debug('PortfolioSizeInput Debug:', {
    portfolioSize,
    totalAllocations,
    hasOverflow,
    isLocked,
    hasAutoSize: !!onAutoSize,
    oldCondition: !isLocked && totalAllocations >= portfolioSize && onAutoSize,
    newCondition: !isLocked && hasOverflow && onAutoSize
  });

  // Use hasOverflow boolean for consistent logic with handleAutoSize function
  const shouldShowAutoSize = !isLocked && hasOverflow && onAutoSize;

  // Plain number formatting (no currency symbol) as per product spec
  const formattedValue = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(portfolioSize) || 0);

  return (
    <div className="w-full max-w-sm">
      <Label htmlFor="portfolioSize">Portfolio Size</Label>
      <div className="relative">
        <Input
          id="portfolioSize"
          type="text"
          value={formattedValue}
          onChange={onPortfolioSizeChange}
          className={`text-lg ${shouldShowAutoSize ? 'pr-24' : ''}`}
        />
        {shouldShowAutoSize && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onAutoSize}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 px-2 text-xs"
            title="Expand portfolio to fit allocations"
          >
            Auto-size
          </Button>
        )}
      </div>
    </div>
  );
};